import {
  availableInstancesAt,
  knownLocations,
  resolveInstanceChoice,
  surveyEquipmentInstances,
  type EquipmentInstanceSurvey,
  type KnownLocation,
  type Workout,
} from '@jackedlogs/shared';

// EQ-1 equipment readers over an already-fetched history window, split out of
// useEntryHistory (CN-EHIST-SPLIT) so the cache module stays under the
// 500-line cap and the equipment DOMAIN RULES — which is what most of this
// file is — live where the EQ-1 work edits them. Same contract as
// ./entryHistoryPick and ./entryHistoryE1rm: no React, no api, no module
// state; the caller owns the cache and passes the window in, nullable so the
// cold-cache answers below stay exactly the ones useEntryHistory gave.

// EQ-1 — which physical machines this exercise has actually been logged on,
// most recently used first. Reads the window the caller hands in, with the
// same cold-cache behaviour it had inside useEntryHistory: an empty list,
// never a throw.
//
// This is why the feature needs no new network call. The window is already
// fetched and already run through assembleWorkout, so the instances are
// sitting in it; the derivation itself lives in shared/equipmentInstances so
// the add path, the replace path and (later) the web mount cannot answer the
// same question differently.
//
// Declared limit: instances unused for longer than the window do not appear.
// Recreating one under the same automatic name rejoins its history, because
// the identity IS the tag value, not a record somewhere.
export function instanceSurveyForSlug(
  workouts: Workout[] | null,
  exerciseId: string,
  /** Where we are now, when known: it RANKS the list, it never trims it. */
  orderByLocation?: string,
): EquipmentInstanceSurvey {
  if (!workouts || workouts.length === 0) return { named: [] };
  return surveyEquipmentInstances(
    workouts,
    exerciseId,
    orderByLocation === undefined ? {} : { orderByLocation },
  );
}

/** Places this athlete has trained at, most recent first. Same window. */
export function knownLocationsIn(workouts: Workout[] | null): KnownLocation[] {
  if (!workouts || workouts.length === 0) return [];
  return knownLocations(workouts);
}

// The instance an entry should carry when NOBODY asked the athlete: the one
// known machine when there is exactly one, otherwise nothing. Both the add
// path and the replace path need this same answer, and they must not be able
// to give different ones — a machine applied silently on add and not on
// replace would split one exercise's history down the middle.
//
// `choose` deliberately resolves to null rather than a guess: two plausible
// machines is exactly the case the design hands to the swap picker, because
// guessing there would dirty the data (equipment-model.md §8.3).
// ⛔ Takes NO location, deliberately (EQ-1 D, 2026-08-13). Silent-apply must
// answer off the GLOBAL known set: "is there exactly one machine this exercise
// has ever been on". Since the place now ORDERS rather than filters
// (knownEquipmentInstances), handing one in would not shrink the list — so a
// caller who passed it expecting the old narrowing would get a silent apply
// decided by the global count, believing it was decided by this gym's. Costs
// nothing to trigger, nothing to notice. The parameter is gone so the mistake
// cannot be made.
// ⛔ Reads `.named` ONLY (EQ-1-DEFAULT, 2026-08-14). The default is offered in
// the tray and never counted here: fold it in and an exercise with one named
// machine would stop applying it silently and start asking instead — a prompt
// nobody decided to add, on the path that exists to raise none.
//
// ⭐ EQ-1-GYM-COLDSTART R6 (2026-08-17) — the SECOND half of the rule above,
// and it does not contradict it. The two paragraphs are about two different
// things that both look like "where you are":
//
//   a PLACE      is an INFERENCE — it orders, it does not filter. It stays out
//                for exactly the reason written above: it would not shrink the
//                list, so a caller handing one in would get a silent apply
//                decided by the GLOBAL count while believing it was decided by
//                this gym's. That reasoning is unchanged and still right.
//   a DECLARATION is a FACT the athlete stated out loud in the ✕ dialog. It
//                genuinely shrinks the candidate set, because the athlete
//                said so. Narrowing by it is not guessing.
//
// ⇒ the `absentHere` parameter below is a list of VALUES, never a place. The
// caller — the only layer that knows where "here" is — resolves it with
// `declaredAbsentInstancesAt`.
//
// ⭐ EQ-1-LASTHERE (Antenore, 2026-08-18) — a place parameter now EXISTS, and
// this is the counter-explanation it owes. The earlier decision was NOT wrong
// and what it forbade is still forbidden: a place NARROWING the candidate set
// behind the reader's back, so that a silent apply decided by the global count
// looks like it was decided by this gym's. `here` does the opposite — it
// decides WHETHER silence may speak (only what was already used at this place)
// while `known` still arrives whole and the tray still lists everything.
// ⛔ It must never fall back to "last used, globally" when nothing was used
// here: that is the Dobedan case the rule was written against.
// 📖 Full reasoning, and the two gaps it closes: equipment-model.md §14.
//
// The rule this yields, and all THREE conditions are load-bearing: apply in
// silence only when exactly one named machine remains AVAILABLE here AND the
// untagged Default carries no history of its own. If the only one known was
// declared absent, resolve to null — attach nothing, ask nothing. Attaching it
// would tag a set with a machine that is not in the room, which is precisely
// the per exercise+tag pollution this whole feature exists to prevent, and it
// would happen with no tap for the athlete to notice.
//
// ⭐ The third condition is EQ-1-DEFAULT-WINS (below) and it is the one that
// fires in practice: an athlete arriving with years of logs has a Default on
// every exercise, so naming a machine adds it beside that history instead of
// silently inheriting it.
export function silentInstanceForSlug(
  workouts: Workout[] | null,
  exerciseId: string,
  /** Values declared absent HERE, already resolved. ⛔ Still never a place. */
  absentHere: readonly string[],
  /** Where the entry is being born, when the caller knows it (EQ-1-LASTHERE).
   *  Selects WHETHER silence may speak; never shrinks the list. */
  here: string | null,
  /** EQ-1-PIN-DEFAULT — what the athlete ELECTED for this exercise here, if
   *  anything. Three-valued: a slug, `null` for the virtual Default, and
   *  `undefined` for "nothing elected, apply the rules below". */
  pinned?: string | null,
): string | null {
  const survey = instanceSurveyForSlug(workouts, exerciseId);
  const available = availableInstancesAt(survey.named, absentHere);
  // EQ-1-PIN-DEFAULT, applied AFTER the absence narrowing and BEFORE the
  // resolver — which is what makes "declared absent beats elected" mechanical
  // rather than a precedence branch somebody has to remember. A machine the
  // athlete elected and then declared missing here is simply not in
  // `available`, so the election finds nothing to apply and the ordinary rules
  // resume.
  if (pinned !== undefined) {
    // The Default elected explicitly: attach nothing, ask nothing. This is the
    // answer, not the absence of one — it is how the athlete says "my usual
    // one, stop asking" on an exercise where two machines would otherwise
    // interrupt every time.
    if (pinned === null) return null;
    const elected = available.find((k) => k.value === pinned);
    if (elected) return elected.value;
    // Elected but not among the candidates: fall through rather than force it.
    // ⚠️ Two real causes, and neither is "used at another place" — the place
    // ORDERS the survey, it never filters it (§14 above). Either the value was
    // declared absent here, or it is not in the survey at all (never logged,
    // or aged out of the window). Stamping a machine that is not in the room is
    // the one thing this whole feature exists to prevent.
  }
  const resolution = resolveInstanceChoice(
    available,
    {
      // ⭐ EQ-1-DEFAULT-WINS — the rule that stops a name from displacing a
      // history. `survey.unnamed` IS "the Default carries sets": it exists
      // only when something was logged before any machine had a name, so its
      // presence is the whole test and no second derivation is needed. When it
      // is there, one named machine resolves to `none` and this returns null:
      // the entry is born on the Default, and the athlete picks the machine
      // when they are actually standing at it.
      // ⚠️ Passed on BOTH paths. On the place-less one it IS the rule; with a
      // place in hand it is the floor under it — the Default competes by its
      // own recency there, but only where that recency exists, and it usually
      // does not: places arrived after the logs. Without this floor a single
      // machine named at a gym would silently displace years of untagged
      // history, which is the P1 EQ-1-DEFAULT-WINS closed.
      defaultCarriesHistory: survey.unnamed !== undefined,
      ...(here === null
        ? {}
        : {
            here,
            ...(survey.unnamed?.lastUsedHere
              ? { defaultLastUsedHere: survey.unnamed.lastUsedHere }
              : {}),
          }),
    },
  );
  return resolution.kind === 'single' ? resolution.instance.value : null;
}
